/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { X, User, BarChart, Activity, DollarSign, ExternalLink, Copy, Check } from 'lucide-react';
import { formatAddress, formatCompact, formatCurrency } from '../lib/utils/format';

interface WalletAnalysisPopupProps {
    walletAddress: string | null;
    onClose: () => void;
}

// Mock data for wallet analysis
const generateMockData = (address: string) => {
    const seed = address.length + address.charCodeAt(2);
    return {
        pnl: (seed * 12345) % 500000,
        winRate: 60 + (seed % 35),
        totalVolume: (seed * 1234567) % 10000000,
        balances: [
            { symbol: 'SOL', amount: (seed % 100) + 10, value: ((seed % 100) + 10) * 145 },
            { symbol: 'USDC', amount: (seed * 100) % 50000, value: (seed * 100) % 50000 },
            { symbol: 'BONK', amount: (seed * 1000000) % 99999999, value: ((seed * 1000000) % 99999999) * 0.000025 },
        ],
        recentActivity: [
            { type: 'BUY', symbol: 'WIF', value: 1250, time: Date.now() - 3600000 },
            { type: 'SELL', symbol: 'POPCAT', value: 850, time: Date.now() - (3600000 * 3) },
            { type: 'MINT', symbol: 'NEWCOIN', value: 50, time: Date.now() - (86400000) },
        ]
    };
};

export const WalletAnalysisPopup: React.FC<WalletAnalysisPopupProps> = ({ walletAddress, onClose }) => {
    const [isCopied, setIsCopied] = useState(false);
    const [data, setData] = useState<ReturnType<typeof generateMockData> | null>(null);

    useEffect(() => {
        if (walletAddress) {
            setData(generateMockData(walletAddress));
        }
    }, [walletAddress]);

    if (!walletAddress || !data) return null;

    const handleCopy = () => {
        navigator.clipboard.writeText(walletAddress);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    return (
        <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div 
                className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col h-full max-h-[70vh] border border-stone-200"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-stone-100 flex justify-between items-center bg-white sticky top-0 z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center text-stone-500">
                            <User size={20} />
                        </div>
                        <div>
                            <h3 className="font-serif text-xl font-bold text-stone-900">Wallet Analysis</h3>
                            <div className="flex items-center gap-2">
                                <p className="text-xs text-stone-500 font-mono">{formatAddress(walletAddress, 8)}</p>
                                <button onClick={handleCopy} className="text-stone-400 hover:text-stone-800">
                                    {isCopied ? <Check size={12} className="text-green-500"/> : <Copy size={12}/>}
                                </button>
                                <a href={`https://solscan.io/account/${walletAddress}`} target="_blank" rel="noreferrer" className="text-stone-400 hover:text-stone-800">
                                    <ExternalLink size={12}/>
                                </a>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full text-stone-400 hover:text-stone-900" title="Close">
                        <X size={20} />
                    </button>
                </div>
                
                <div className="flex-1 overflow-y-auto custom-scrollbar p-6 bg-stone-50/50">
                    <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-xl border border-stone-200 text-center">
                            <div className="text-[10px] uppercase font-bold text-stone-400 mb-1">PnL</div>
                            <div className={`font-mono font-bold text-lg ${data.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>${formatCompact(data.pnl)}</div>
                        </div>
                        <div className="bg-white p-4 rounded-xl border border-stone-200 text-center">
                            <div className="text-[10px] uppercase font-bold text-stone-400 mb-1">Win Rate</div>
                            <div className="font-mono font-bold text-lg text-stone-900">{data.winRate}%</div>
                        </div>
                        <div className="bg-white p-4 rounded-xl border border-stone-200 text-center">
                            <div className="text-[10px] uppercase font-bold text-stone-400 mb-1">Volume</div>
                            <div className="font-mono font-bold text-lg text-stone-900">${formatCompact(data.totalVolume)}</div>
                        </div>
                    </div>

                    <div className="mb-6">
                        <h4 className="text-xs font-bold text-stone-500 uppercase mb-2 flex items-center gap-2"><DollarSign size={14}/> Top Balances</h4>
                        <div className="space-y-2">
                            {data.balances.map(b => (
                                <div key={b.symbol} className="bg-white p-3 rounded-lg border border-stone-100 flex justify-between items-center">
                                    <span className="font-bold text-sm text-stone-800">{b.symbol}</span>
                                    <div className="text-right">
                                        <div className="font-mono text-sm font-bold text-stone-900">{formatCompact(b.amount)}</div>
                                        <div className="text-[10px] text-stone-400 font-mono">{formatCurrency(b.value)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                     <div>
                        <h4 className="text-xs font-bold text-stone-500 uppercase mb-2 flex items-center gap-2"><Activity size={14}/> Recent Activity</h4>
                        <div className="space-y-2">
                            {data.recentActivity.map((act, i) => (
                                <div key={i} className="bg-white p-3 rounded-lg border border-stone-100 flex justify-between items-center">
                                    <div>
                                        <div className={`text-xs font-bold uppercase ${act.type === 'BUY' ? 'text-green-600' : act.type === 'SELL' ? 'text-red-600' : 'text-purple-600'}`}>{act.type}</div>
                                        <div className="font-bold text-sm text-stone-800">{act.symbol}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-mono text-sm font-bold text-stone-900">{formatCurrency(act.value)}</div>
                                        <div className="text-[10px] text-stone-400">{Math.round((Date.now() - act.time) / 3600000)}h ago</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
